import React, {Fragment, useContext, useState, useCallback} from "react";
import {FormattedMessage, useIntl} from "react-intl";
import Button from "../ui-components/Button";
import ConfiguratorSurveyOutput from "./ConfiguratorSurveyOutput";
import filterActiveSteps from "../utils/filterActiveSteps";
import ConfiguratorContext from "./ConfiguratorContext";
import ConfiguratorSurveyBlockHeader from "./ConfiguratorSurveyBlockHeader";
import ConfiguratorSurveySummaryStepProductsLinks from "./ConfiguratorSurveySummaryStepProductsLinks";
import {storeConfigurationKpi} from "../utils/storeConfigurationKpi";
import useGetOptionTitle from "../hooks/useGetOptionTitle";
import {LockClosedIcon, LockOpenIcon} from "@heroicons/react/24/outline";
import useCountryAxiosInstance from "../hooks/useCountryAxiosInstance";
import {NotificationsContext} from "../ui-components/Notifications";
import evaluateBlockDependencies from "../utils/evaluateBlockDependencies";
import explodeSurveyResponses from "../utils/explodeSurveyResponses";
import {CalculatorDollarIcon} from "@ui-components/icons";
import {CreateQuotationFromDesignModal} from "../feature/worksites";
import CountryContext from "../CountryContext";
import {useGenerateQuotationSurvey} from "../feature/surveys";
import Spinner from "@ui-components/Spinner";
import Icon from "@ui-components/Icon";
import getTCSAttachments from "../utils/getTCSAttachments";
import TCSDialog from "./TCSDialog";
import UserNotificationsContext from "../UserNotificationsContext";
import {SurveyOldVersionWarning} from "@components/SurveyOldVersionWarning";
import { TCSSurveyModal } from "./Modals/TCSSurveyModal";

const createTaskNotifications = async (
  api,
  {service, survey, responses, configuration},
  onNotificationCreation=undefined,
) => {
  if (!survey.worksite_id)
    return;

  const checklists = await api.get(`/services_checklists/all`, {params: {id_service: service.id, flg_active: true}});

  // if any (possibly exploded) response group matches any checklist's dependencies, add a notification meaning that
  // there's at least one task suggestion ready to be implemented
  const {groups} = explodeSurveyResponses(Object.values(responses), configuration, false);
  const atLeastOneMatch = checklists.some(
    (c) => groups.some(
      (g) => evaluateBlockDependencies(c.dependencies, {configuration, responses: g})
    )
  );
  if (atLeastOneMatch) {
    await api.post(`/worksites_tasks/notification/${survey.worksite_id}`, survey.id);
    if (onNotificationCreation)
      onNotificationCreation();
  }
}

const ConfiguratorSurveySummaryStep = ({history, returnPath, role, associatedServices = []}) => {
  // context
  const context = useContext(ConfiguratorContext);
  const {country} = useContext(CountryContext);
  const {push} = useContext(NotificationsContext);
  const { refreshNotifications } = useContext(UserNotificationsContext);

  // hooks
  const intl = useIntl();
  const countryApiRequest = useCountryAxiosInstance();
  const {
    dispatch: generateQuotation,
    loading: generatingQuotation,
  } = useGenerateQuotationSurvey();

  // state
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [quotationSurveyCreationModalOpen, setQuotationSurveyCreationModalOpen] = useState(false);
  // state for TCS
  const [sendingToTCS, setSendingToTCS] = useState(false);
  const [sentToTCSSuccess, setSentToTCSSuccess] = useState(false);
  const [sentToTCSFailure, setSentToTCSFailure] = useState(false);
  const [showTCSDialog, setShowTCSDialog] = useState(false);
  const [showTCSModal, setShowTCSModal] = useState(false);

  const [notes, setNotes] = useState('');
  const [flgGdpr, setFlgGdpr] = useState(false);
  const [flgMarketing, setFlgMarketing] = useState(false);

  // helpers
  const TintometerCloseBtnIcon = !context?.survey?.closed ? LockClosedIcon : LockOpenIcon;
  const getOptionTitle = useGetOptionTitle();
  const atLeastOneAttachToTCS= Object.values(context.responses)
    .map(r=>r.question.attach_to_tcs).some(x=>x);
  const closeSummary = async () => {
    if (!!context?.survey) {  // skip this if it's being called from a survey preview (explorer)
      setSaving(true);
      try {
        await createTaskNotifications(
          countryApiRequest,
          context,
          () => { refreshNotifications();
          });
        await storeConfigurationKpi(context, getOptionTitle, countryApiRequest)
      } catch (err) {
        push({type: 'error', title: intl.formatMessage({id: 'an_error_occurred'})})
        console.log('ERROR IN CONFIGURATION KPI', err)
      } finally {
        setSaving(false);
      }
    }

    history.push(returnPath)
  }

  const sendToTCS = async () => {
    if (!!context?.survey) {
      const recQuestionsTableMode = Object.values(context.responses)
        .filter(v => v.question.type === "recursive_question"
          && v.question.attach_to_tcs)


      const nodeReinforcement = Object.values(context.responses)
        .find(v => v.question.input_type === 'node_reinforcement_input'
          && v.question.attach_to_tcs)

      let attachments = getTCSAttachments(recQuestionsTableMode, nodeReinforcement, getOptionTitle)
      setShowTCSDialog(false)
      setSendingToTCS(true)
      try {
        let response = await countryApiRequest.post('/webtocase/steelcombo',{
          "worksiteId": context.survey.worksite_id,
          "surveyId": context.survey.id,
          "webPrivacy": flgGdpr,
          "webMarketing": flgMarketing,
          "webDescription": notes,
          "attachments": attachments
        });
        setSendingToTCS(false);
        setSentToTCSSuccess(true)
      } catch (err) {
        push({type: 'error', title: intl.formatMessage({id: 'an_error_occurred'})})
        console.log('ERROR IN SENDING DATA TO TCS', err)
        setSendingToTCS(false);
        setSentToTCSFailure(true)
      }
    }
  }
  const flipConfigurationOpenStatus = async () => {
    setSending(true);
    try {
      const updatedSurvey = await countryApiRequest.put(`/service_surveys/${context.survey.id}`, {
        ...context.survey,
        closed: !context?.survey?.closed
      });
      context?.setSurvey(updatedSurvey);
      push({type: 'success', title: <FormattedMessage id="configuration_edited_successfully"/>});
    } catch (err) {
      push({type: 'error', title: <FormattedMessage id="an_error_occurred"/>});
      throw err;
    } finally {
      setSending(false);
    }
  }

  const canShowTCSModal = (context, service) => {
    if(!context || !service || !service.metadata_?.TCS) return false;
    const isActive = service?.metadata_?.TCS?.active;

    const dependencies = service?.metadata_?.TCS?.dependencies;
    // Check if are not dependencies or if they are validated
    const areDependenciesValidated =
      !dependencies.length || evaluateBlockDependencies(dependencies, context);

    return isActive && areDependenciesValidated;
  };

  return (
    <>
      <div className="flex flex-col gap-8">
        <h2 className="text-4xl text-kkgreen-00">
          <FormattedMessage id="summary" defaultMessage="Summary" />
        </h2>
        <SurveyOldVersionWarning
          type="alert"
          name={context?.service?.name}
          version={context?.survey?.service_version}
          latest={context?.service?.active_version}
        />
        <div>
          {filterActiveSteps(context).map(
            ({
              title,
              description,
              image_filename,
              blocks,
              id,
              hidden_question,
            }) => {
              const hidden = hidden_question.includes(`role_type_${role}`)
                ? "hidden"
                : "";
              return (
                <div
                  key={id}
                  className={`border-b-2 border-black pb-4 mb-8 flex-col flex w-full ${hidden}`}
                >
                  <ConfiguratorSurveyBlockHeader
                    title={title}
                    description={description}
                    image_filename={image_filename}
                    hidden={hidden}
                  />
                  {blocks.map((output) => (
                    <ConfiguratorSurveyOutput
                      summary
                      key={output.id}
                      output={output}
                      role={role}
                    />
                  ))}
                </div>
              );
            }
          )}
          <ConfiguratorSurveySummaryStepProductsLinks role={role} />
          <div className="w-full flex justify-end gap-3 mt-4 mb-20 md:mb-2">
            {associatedServices.length > 0 && (
              <Button
                styleType="empty"
                onClick={() => {
                  setQuotationSurveyCreationModalOpen(true);
                }}
                Icon={CalculatorDollarIcon}
              >
                <FormattedMessage id="create_quotation_survey" />
              </Button>
            )}
            {context?.survey?.worksite_id === null && (
              <Button
                styleType="empty"
                onClick={async () => {
                  await flipConfigurationOpenStatus();
                }}
                submitting={sending}
              >
                <TintometerCloseBtnIcon className="w-4" />
                <FormattedMessage
                  id={`mark_configuration_as_${
                    !context?.survey?.closed ? "closed" : "open"
                  }`}
                />
              </Button>
            )}

            {atLeastOneAttachToTCS ? (
              sendingToTCS ? (
                <div className="relative w-10 h-10">
                  <Spinner styleType="white" />
                </div>
              ) : sentToTCSSuccess ? (
                <div className={"flex"}>
                  <div className="mt-2 mr-1">
                    <FormattedMessage id="sent_to_TCS_successfully" />
                  </div>
                  <Icon
                    className="w-10 h-10"
                    iconName={"CheckCircleIcon"}
                    color={"green"}
                  ></Icon>
                </div>
              ) : sentToTCSFailure ? (
                <div className={"flex"}>
                  <div className="mt-2 mr-1">
                    <FormattedMessage id="error_sending_to_TCS" />
                  </div>
                  <Icon
                    className="w-10 h-10"
                    iconName={"XCircleIcon"}
                    color={"red"}
                  ></Icon>
                </div>
              ) : (
                <Button
                  styleType="secondary"
                  onClick={() => {
                    setShowTCSDialog(true);
                  }}
                >
                  <FormattedMessage id="send_to_tcs" />
                </Button>
              )
            ) : null}

            { canShowTCSModal(context, context?.service) ? (
              <>
                <Button
                  styleType="empty"
                  onClick={async () => {
                    setShowTCSModal(true);
                  }}
                  submitting={sending}
                >
                  <FormattedMessage id="request_support_tcs" defaultMessage="Richiesta al TCS"/>
                </Button>
                {showTCSModal && (
                  <TCSSurveyModal
                    metadata={context?.service?.metadata_?.TCS}
                    setShowModal={setShowTCSModal}
                    loading={sendingToTCS}
                    setLoading={setSendingToTCS}
                    fromService={true}
                  />
                )}
              </>
            ) : null}

            <Button
              id="close-survey-button"
              onClick={closeSummary}
              submitting={saving}
            >
              <FormattedMessage id="close_summary" />
            </Button>
          </div>
        </div>
        {showTCSDialog && (
          <TCSDialog
            setShowTCSDialog={setShowTCSDialog}
            notes={notes}
            setNotes={setNotes}
            flgGdpr={flgGdpr}
            flgMarketing={flgMarketing}
            setFlgGdpr={setFlgGdpr}
            setFlgMarketing={setFlgMarketing}
            sendToTCS={sendToTCS}
          />
        )}
        {quotationSurveyCreationModalOpen && (
          <CreateQuotationFromDesignModal
            quotationServices={associatedServices}
            onConfirm={async (qsid) => {
              const { survey } = context;
              const { worksite_id: wid } = survey;
              const name = `${survey.name} - ${intl.formatMessage({
                id: "quotation_service",
              })}`;
              const qsav = associatedServices.find(
                (a) => a.id === qsid
              )?.active_version;
              if (!qsav) {
                push({
                  type: "error",
                  title: intl.formatMessage({ id: "an_error_occurred" }),
                });
                return;
              }
              const newSurvey = await generateQuotation(
                [survey],
                qsid,
                qsav,
                name
              );
              // close modal and navigate to new survey
              await closeSummary();
              setQuotationSurveyCreationModalOpen(false);
              history.push(
                `/${country.code}/worksites/${wid}/service/${qsid}/survey/${newSurvey.id}`
              );
            }}
            onClose={() => {
              setQuotationSurveyCreationModalOpen(false);
            }}
            submitting={generatingQuotation}
          />
        )}
      </div>
    </>
  );
};

export default ConfiguratorSurveySummaryStep;
